import { z } from 'zod';

import { timestampSchema } from './common';
import { uidSchema } from './ids';

/**
 * Roles as the application understands them.
 *
 * The authoritative source is the Firebase **custom claim** on the ID token,
 * never a field in Firestore. A user document can be edited by whoever may
 * write it; a token claim can only be set with the Admin SDK. Storing the role
 * in Firestore would therefore be an escalation path, so it is not stored.
 */
export const userRoleSchema = z.enum(['captain', 'admin']);
export type UserRole = z.infer<typeof userRoleSchema>;

/**
 * Profile mirror at `users/{uid}`, written on sign-in.
 *
 * A user's team is *not* stored here — it is found by querying teams where
 * `captainUid == uid`. With at most a few hundred teams that query is trivial,
 * and it removes a duplicated reference that could go stale.
 */
export const userProfileSchema = z.object({
  uid: uidSchema,
  email: z.email().nullable(),
  displayName: z.string().trim().max(120).nullable(),
  photoUrl: z.url().max(500).nullable(),
  createdAt: timestampSchema,
  lastLoginAt: timestampSchema,
});

export type UserProfile = z.infer<typeof userProfileSchema>;

/** The signed-in user as the UI sees them, role included. */
export interface AuthenticatedUser {
  readonly profile: UserProfile;
  readonly role: UserRole;
}
